import { voyageHasDelay, voyageIsAhead } from './voyageDelay.js';

/**
 * The states the Shipment status chip offers, in order.
 *
 * `at_port` and `discharged` are deliberately absent: the review drops them
 * from the filter and the legend. `shipmentStatusKey` below still returns
 * them, and it must — rows in those states keep their colour on the map. This
 * list governs only what the chip offers, and `passesChipFilters` lets every
 * row through while nothing is selected, so dropping them here hides no
 * shipments.
 */
export const SHIPMENT_STATUS_KEYS = ['on_time', 'ahead', 'delayed'];

/**
 * Status of one row. The map markers colour themselves from this same function
 * — modeIcons re-exports it as `statusToIconState` — so a chip selection can
 * never disagree with the icon the user is looking at.
 *
 * Delayed / ahead come from `voyageDelay`, the same derivation the vessel
 * markers and StatusBadge use: the max delay of the cargo still aboard. The
 * `delayed` / `ahead` booleans this used to read are not fields the API sends,
 * so the chip's Delayed and Ahead rows could never match a single shipment.
 *
 * Air and truck rows carry no delay signal at all, so they keep falling through
 * to the status string exactly as before.
 */
export const shipmentStatusKey = (row) => {
  if (!row) return 'on_time';
  if (voyageHasDelay(row)) return 'delayed';
  if (voyageIsAhead(row)) return 'ahead';
  const status = String(row.status || '').toLowerCase();
  if (status === 'discharged') return 'discharged';
  if (status === 'arrived' || status === 'at_port') return 'at_port';
  return 'on_time';
};

/**
 * Party names on a row. Sea rows carry a singular `exporter` / `importer`
 * string; air and truck rows carry comma-joined `exporters` / `importers`.
 */
const partyList = (row, singular, plural) =>
  String(row?.[singular] ?? row?.[plural] ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

export const shipmentExporters = (row) => partyList(row, 'exporter', 'exporters');
export const shipmentImporters = (row) => partyList(row, 'importer', 'importers');

/** Match key: upper-cased, so "Dole" and "DOLE" are one option. */
export const partyKey = (v) => String(v ?? '').trim().toUpperCase();

/**
 * Option list for a party chip: one entry per distinct name, first spelling
 * seen wins the label, sorted for a stable dropdown.
 */
export const partyOptions = (rows, pick) => {
  const seen = new Map();
  rows.forEach((row) => {
    pick(row).forEach((name) => {
      const id = partyKey(name);
      if (id && !seen.has(id)) seen.set(id, name);
    });
  });
  return [...seen]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Chip selections are `null` when unrestricted, an array once the user picks.
 *
 * The click rule every map filter chip shares. An unrestricted chip draws all
 * of its rows checked, so a click there reads as "show me this one", not "hide
 * this one" — it isolates. It used to start from the full list and remove, which
 * meant clicking Delayed was the one gesture guaranteed to hide every delayed
 * shipment. Once a real selection exists, clicks add and remove normally.
 *
 * Callers whose unrestricted state is a mode rather than `null` (origin,
 * destination, transport mode) pass `mode === 'all' ? null : selectedKeys`.
 */
export const toggleSelection = (selected, id) => {
  if (selected == null) return [id];
  return selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id];
};

/** Trigger text for a chip: All / None / first name (+N). */
export const chipSummary = (selected, options) => {
  if (selected == null || selected.length === options.length) return { kind: 'all' };
  if (selected.length === 0) return { kind: 'none' };
  const labels = options.filter((o) => selected.includes(o.id)).map((o) => o.label);
  const extra = labels.length - 1;
  return { kind: 'names', names: labels[0] + (extra > 0 ? ` +${extra}` : '') };
};

const passesAxis = (values, selected) => {
  if (selected == null) return true;
  if (selected.length === 0) return false;
  return values.some((v) => selected.includes(partyKey(v)));
};

/**
 * One row against the whole chip row. Every axis is unrestricted until the
 * user narrows it, so a freshly loaded map shows everything.
 */
export const passesChipFilters = (row, { statuses, importers, exporters } = {}) => {
  if (statuses != null && !statuses.includes(shipmentStatusKey(row))) return false;
  if (!passesAxis(shipmentImporters(row), importers)) return false;
  if (!passesAxis(shipmentExporters(row), exporters)) return false;
  return true;
};
